'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { type JSX, useCallback, useRef } from 'react';

import { Label, TextAreaInput, TextAreaInputProps } from '@videoblocks/design-system';

import { SuggestionRow } from './SuggestionRow';

export type Suggestion = { label: string; promptText: string };

export type TextAreaInputFieldProps = TextAreaInputProps & {
    suggestions?: Suggestion[];
    onSuggestionClick?: (suggestion: Suggestion) => void;
};

export const TextAreaInputField = ({
    suggestions,
    onSuggestionClick,
    disabled,
    ...rest
}: TextAreaInputFieldProps): JSX.Element => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSuggestionClick = useCallback(
        (suggestion: Suggestion): void => {
            const el = textareaRef.current;
            if (el) {
                el.focus();
                el.setSelectionRange(0, el.value.length);
                try {
                    document.execCommand('insertText', false, suggestion.promptText);
                } catch {
                    datadogLogs.logger.warn('execCommand insertText unavailable', {
                        component: 'TextAreaInputField',
                    });
                }
            }
            onSuggestionClick?.(suggestion);
        },
        [onSuggestionClick],
    );

    const hasSuggestions = suggestions && suggestions.length > 0;

    const textAreaInput = (
        <TextAreaInput
            ref={textareaRef}
            showCount
            textAreaClassName="min-w-0 field-sizing-fixed"
            resize="none"
            id="text-prompt"
            name="text-prompt"
            disabled={disabled}
            className={hasSuggestions ? 'z-10' : undefined}
            {...rest}
        />
    );

    return (
        <div className="flex flex-col gap-2 w-full">
            <Label htmlFor="text-prompt" className="text-left" fontSize="sm">
                Prompt
            </Label>
            <div className="flex flex-col w-full">
                {hasSuggestions && (
                    <SuggestionRow
                        suggestions={suggestions}
                        disabled={disabled}
                        onSuggestionClick={handleSuggestionClick}
                    />
                )}
                {textAreaInput}
            </div>
        </div>
    );
};
